// core
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {BrowserModule} from '@angular/platform-browser';
import {FlexLayoutModule} from '@angular/flex-layout';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {HttpClient, HttpClientModule} from '@angular/common/http';
import {NgModule} from '@angular/core';
import {registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import localeFr from '@angular/common/locales/fr';
import localeNl from '@angular/common/locales/nl';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {NgxCaptchaModule} from 'ngx-captcha';

// UI
import {AlertModule} from 'ngx-bootstrap/alert';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {NgSelectModule} from '@ng-select/ng-select';

// components
import {AppComponent} from './app.component';
import {UserRegistrationComponent} from './user-registration/user-registration.component';
import {AdminUsersOverviewComponent} from './admin-users-overview/admin-users-overview.component';

// services
import {CustomHttp} from './core/http-custom/http-custom.service';
import {UtilsService} from './utils/utils.service';
import {AdminUserOverviewService} from './admin-users-overview/service/admin-users.service';
import {UserPermissionsService} from './permissions/user-permissions.service';
import {CustomerService} from './customer/services/customer-service.service'
import {RolesUtils} from './utils/roles.utils';

// modules
import {AppRoutingModule} from './app-routing.module';
import {CoreModule} from './core/core.module';
import {SharedModule} from './shared/shared.module';

// UI
import {ModalModule} from 'ngx-bootstrap/modal';
import {BsDropdownModule} from 'ngx-bootstrap/dropdown';
import {ImageCropperModule} from 'ngx-image-cropper';

import {UserResolve} from './user/user.resolve';
import {UserDetailService} from './user/service/user-detail.service';
import {RolesService} from './role/roles.service';
import {EntryPageComponent} from './home-page/home-page.component';
// import {ChangePasswordComponent} from './change-password/change-password.component';
import {ForgotPasswordComponent} from './forgot-password/forgot-password.component';
import {ConfirmationPageComponent} from './confirmation-page/confirmation-page.component';
import {EmailConfirmationComponent} from './email-confirmation/email-confirmation.component';
import {UpdateMyProfileComponent} from './update-my-profile/update-my-profile.component';
import {UpdateProfileService} from 'app/update-my-profile/services/update-my-profile.service';
import {SlxAdminManageCustomersComponent} from './slx-admin-manage-customers/slx-admin-manage-customers.component';
import {SlxAdminCustomersService} from './slx-admin-manage-customers/service/slx-admin-customers.service';
import {MyProfileChangePasswordComponent} from './my-profile-change-password/my-profile-change-password.component';
import {ConfirmationModal} from './shared/dialogs/confirmation-modal/confirmation-modal.component';
import {ErrorModal} from './shared/dialogs/error-modal/error-modal.component';
import {SlxAdminManageUsersComponent} from './slx-admin-manage-users/slx-admin-manage-users.component';
import {SlxAdminUsersService} from './slx-admin-manage-users/service/slx-admin-users.service';
import {ErrorPageComponent} from './error-page/error-page.component';
import {ResetPasswordComponent} from './reset-password/reset-password.component';
import {MyCompanyDataComponent} from './my-company-data/my-company-data.component';
import {CompanyDataServiceService} from './my-company-data/services/company-data-service.service';
import {AuthDetailsService} from './auth/auth-details.service';
import {CreateEditCustomerComponent} from './customer/create-edit-customer/create-edit-customer.component';
import {NewsletterAgreementComponent} from './newsletter-agreement/newsletter-agreement.component';
import {UserModule} from './user/user.module';
import {ContactPersonComponent} from './contact-person/contact-person.component';
import {AuthGuardService} from './guards/auth-guard.service';

import {environment} from '../environments/environment';


// locale data for the date formats of the UI languages, see LocalizedDatePipe; 'en' is built into Angular
registerLocaleData(localeDe);
registerLocaleData(localeFr);
registerLocaleData(localeNl);

// AoT requires an exported function for factories
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

/**
 * @description Modularity system which helps organize the app into cohesive blocks of functionality
 * @example https://angular.io/guide/architecture#modules
 * @tutorial https://angular.io/guide/ngmodule
 */
@NgModule({
    declarations: [
        AppComponent,
        UserRegistrationComponent,
        AdminUsersOverviewComponent,
        EntryPageComponent,
        // ChangePasswordComponent,
        ForgotPasswordComponent,
        ConfirmationPageComponent,
        EmailConfirmationComponent,
        UpdateMyProfileComponent,
        SlxAdminManageCustomersComponent,
        MyProfileChangePasswordComponent,
        ConfirmationModal,
        ErrorModal,
        SlxAdminManageUsersComponent,
        ErrorPageComponent,
        ResetPasswordComponent,
        MyCompanyDataComponent,
        CreateEditCustomerComponent,
        NewsletterAgreementComponent,
        ContactPersonComponent,
    ],
    imports: [
        NgbModule,
        AlertModule.forRoot(),
        BrowserAnimationsModule,
        BsDropdownModule.forRoot(),
        HttpClientModule,
        TranslateModule.forRoot({
            loader: {
                provide: TranslateLoader,
                useFactory: HttpLoaderFactory,
                deps: [HttpClient],
            }
        }),
        UserModule,
        BrowserModule,
        CoreModule,
        SharedModule,
        FlexLayoutModule,
        FormsModule,
        ReactiveFormsModule,
        NgSelectModule,
        ImageCropperModule,
        ModalModule.forRoot(),
        /**
         * @description AppRoutingModule should always be imported last
         * @example https://angular.io/guide/router#module-import-order-matters
         * @tutorial https://angular.io/guide/router#module-import-order-matters
         */
        AppRoutingModule,
        NgxCaptchaModule
    ],
    exports: [],
    providers: [
        AdminUserOverviewService,
        CustomHttp,
        TranslateService,
        UtilsService,
        RolesUtils,
        UserResolve,
        UserDetailService,
        RolesService,
        UpdateProfileService,
        UserPermissionsService,
        SlxAdminCustomersService,
        SlxAdminUsersService,
        CustomerService,
        CompanyDataServiceService,
        AuthDetailsService,
        AuthGuardService,
    ],
    bootstrap: [
        AppComponent
    ]
})

export class AppModule {
}
